import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import MainLayout from "@/layouts/MainLayout";
import { SidebarProvider } from "@/context/SidebarContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "./components/ui/toaster";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useErrorHandler } from "@/hooks/useErrorHandler";

// Pages
import Home from "@/pages/Home";
import PatientDetailPage from "@/pages/PatientDetailPage";
import AppointmentListPage from "@/pages/AppointmentsPage";
import BillingPage from "@/pages/BillingPage";
import PrescriptionListPage from "@/pages/PrescriptionsPage";
import EditPrescriptionPage from "@/pages/EditPrescriptionPage";
import SettingsPage from "@/pages/SettingsPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SignUpPage from "@/pages/SignUpPage";
import TemplateManagementPage from "./pages/TemplateManagementPage";
import PatientManagementPage from "./pages/PatientManagementPage";
import PedsCalculatorPage from "@/pages/PedsCalculatorPage";
import NotFoundPage from "./pages/NotFoundPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

// IPD
import IPDPage                from "@/pages/IPD/IPDPage";
import IPDAdmissionDetailPage from "@/pages/IPD/IPDAdmissionDetailPage";
import IPDSettingsPage        from "@/pages/IPD/IPDSettingsPage";

// Reports
import ReportsPage from "@/pages/ReportsPage";

// Receptionist Pages
import FrontDeskCommandCenter from "@/pages/frontdesk/FrontDeskCommandCenter";
import FrontDeskPatientPage from "@/pages/FrontDeskPatientPage";
import FrontDeskReportsPage from "@/pages/frontdesk/FrontDeskReportsPage";
import TokenDisplayPage from "@/pages/frontdesk/TokenDisplayPage";

function AppContent() {
  useErrorHandler({
    showToast: true,
    logToConsole: true,
    logToService: process.env.NODE_ENV === 'production'
  });

  return (
      <Router>
        <Routes>
          {/* Public Routes - No Layout */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Protected Routes - With MainLayout */}
          <Route element={<MainLayout />}>
            <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Home />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/patients"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <PatientManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/patients/:id"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <PatientDetailPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/appointments"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <AppointmentListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/prescriptions"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <PrescriptionListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/prescriptions/edit/:id"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <EditPrescriptionPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/library"
                element={
                  <Navigate to="/lab-tests" replace />
                }
            />
            <Route
                path="/billing"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin", "staff"]}>
                    <ErrorBoundary>
                      <BillingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/templates"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <TemplateManagementPage/>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/peds-calculator"
                element={
                  <ProtectedRoute requireClinicalProfile>
                    <ErrorBoundary>
                      <PedsCalculatorPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={["admin", "doctor"]}>
                    <ErrorBoundary>
                      <SettingsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />

            {/* IPD Routes */}
            <Route
                path="/ipd"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin", "staff"]}>
                    <ErrorBoundary>
                      <IPDPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/ipd/admissions/:admissionId"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin", "staff"]}>
                    <ErrorBoundary>
                      <IPDAdmissionDetailPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            {/* Reports */}
            <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin", "staff"]}>
                    <ErrorBoundary>
                      <ReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />

            <Route
                path="/ipd/settings"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin", "staff"]}>
                    <ErrorBoundary>
                      <IPDSettingsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />

            {/* Front Desk Routes */}
            <Route
                path="/frontdesk"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <FrontDeskCommandCenter />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route path="/frontdesk/queue" element={<Navigate to="/frontdesk" replace />} />
            <Route
                path="/frontdesk/patients"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <PatientManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/patients/new"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <PatientManagementPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/patients/:patientId"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <FrontDeskPatientPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/appointments"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <AppointmentListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/appointments/new"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <AppointmentListPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/reports"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <FrontDeskReportsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/frontdesk/billing"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ErrorBoundary>
                      <BillingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <ChangePasswordPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
            <Route
                path="/platform-admin"
                element={
                  <ProtectedRoute requirePlatformAdmin>
                    <ErrorBoundary>
                      <PlatformAdminPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
            />
          </Route>

          {/* Token display — no sidebar/topbar, designed for TV screens */}
          <Route
            path="/token-display"
            element={
              <ProtectedRoute allowedRoles={["staff", "admin"]}>
                <TokenDisplayPage />
              </ProtectedRoute>
            }
          />

          {/* 404 - Outside MainLayout, No Sidebar/Header */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster />
      </Router>
  );
}

export default function App() {
  return (
      <ErrorBoundary
          onError={(error, errorInfo) => {
            console.error('Global app error:', error, errorInfo);

            if (process.env.NODE_ENV === 'production') {
              // Send to monitoring service
            }
          }}
      >
        <ThemeProvider>
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </ThemeProvider>
      </ErrorBoundary>
  );
}